import { emitter, EventType } from '@/utils/emitter.utils';
import { PhotoAlbumBg } from './photo-album-bg';
import { TextAttribute } from './text';
import { SlotAttribute } from './slot';
import { WidgetAttribute } from './widget';
import { ImageArrayAttribute } from './image-array';
import { useImagesStore } from '@/store/use-images-store';
import { getImageSize } from '@/utils/fs/image.utils';
import { ImageAttribute } from './image';
import { SpriteAttribute } from './sprite';
import { ProgressBarAttribute } from './progress-bar';
import { AppWidgetAttribute } from './app-widget';
import { JsFileAttribute } from './js-file';
import { apkFileAttribute } from './apk-file';
import { Shape, ShapeType } from '..';
import { dataSourceWholeItems } from '@/resources/data-source';
import { KonvaUtils } from '@/services/konva/konva.utils';
import { GlobalConfig } from '@/services/gloabl.config';
import { canUse } from '@/utils/can-use.utils';
import { round } from 'lodash-es';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { supportTemperatureType } from '@/utils/ast/utils/image-array.utils';
import { TextWatchfaceAttribute } from './text-watchface';
import { RiveAttribute } from './rive';
import { VideoWatchfaceAttribute } from './video-watchface';

export type Attributes = VideoWatchfaceAttribute &
  PhotoAlbumBg &
  TextWatchfaceAttribute &
  TextAttribute &
  SlotAttribute &
  WidgetAttribute &
  ProgressBarAttribute &
  ImageAttribute &
  ImageArrayAttribute &
  WidgetAttribute &
  SlotAttribute &
  SpriteAttribute &
  AppWidgetAttribute &
  JsFileAttribute &
  RiveAttribute &
  apkFileAttribute;

export type ResourceKey = keyof Attributes['resources'];

export default class ShapeAttribute {
  declare id: string;
  get parent(): Shape | undefined {
    return;
  }
  get layer(): StoreLayerItem | undefined {
    return;
  }

  declare type: ShapeType;
  attrs: Partial<Attributes> = {};

  // 复杂功能不支持xy为负
  get minXY() {
    return this.parent
      ? 0
      : canUse('layout-negative')?.valid && this.type !== ShapeType.AppWidget
      ? -99
      : 0;
  }
  get maxXY() {
    return 999;
  }

  /**
   * 修改普通类型数据（不包括 images unitIcon等图片类型）
   * @param data
   */
  private _setAttrs(data: Partial<Attributes>) {
    const layer = this.layer;
    if (!layer) return;
    layer.attrs = {
      ...layer?.attrs,
      ...data
    };
  }

  /**
   * 修改普通类型数据（不包括 images unitIcon等图片类型）
   * @param data
   */
  setAttrs(data: Partial<Attributes>) {
    // 修复: 当修改图片类型的pivotX或pivotY时，同时调整x和y以保持图片位置不变
    if (this.type === ShapeType.Image && (data.pivotX !== undefined || data.pivotY !== undefined)) {
      // 使用工具函数获取默认 pivot，基于实际渲染宽高（考虑 runtime.width/height）
      const defaultPivot = KonvaUtils.getImageDefaultPivot(this as unknown as Shape);
      const oldPivotX = this.attrs.pivotX ?? defaultPivot.x;
      const oldPivotY = this.attrs.pivotY ?? defaultPivot.y;
      const newPivotX = data.pivotX !== undefined ? data.pivotX : oldPivotX;
      const newPivotY = data.pivotY !== undefined ? data.pivotY : oldPivotY;

      // 计算pivot的变化量
      const deltaPivotX = newPivotX - oldPivotX;
      const deltaPivotY = newPivotY - oldPivotY;

      // 调整x和y以保持图片在画布上的实际位置不变
      // 因为在konva.layout.ts中会执行 baseAttrs.x = x + offsetX
      // 所以当offsetX增加时，需要减少x来保持位置不变
      const newData = { ...data };
      if (data.x === undefined) {
        newData.x = (this.attrs.x ?? 0) - deltaPivotX;
      }
      if (data.y === undefined) {
        newData.y = (this.attrs.y ?? 0) - deltaPivotY;
      }

      this._setAttrs(newData);
      this.initAttrs({ attrs: Object.keys(newData) as (keyof Attributes)[] });
    } else {
      this._setAttrs(data);
      this.initAttrs({ attrs: Object.keys(data) as (keyof Attributes)[] });
    }
  }

  /**
   * 获取资源数据
   * @param resourceName 资源名称
   * @param colorGroup 所属颜色
   * @param base64 [可选] 是否返回base64格式
   * @returns
   */
  getResources(data: {
    resourceName: ResourceKey;
    colorGroup: string;
    base64?: boolean;
  }) {
    const res =
      this.attrs.resources?.[data.resourceName]?.[data.colorGroup] ?? [];
    const imageStore = useImagesStore();
    return data.base64
      ? res.map((item) => imageStore.imagesMap[item]?.value ?? '')
      : res;
  }

  /**
   * 修改资源,
   * @param {Boolean} setSize [可选]是否将图片的宽度和高度赋值给shape的宽度和高度
   */
  async uploadResource(
    data: {
      resourceName: ResourceKey;
      /**
       * 配色id
       */
      colorGroup: string;
      /**
       * 图片真实目录名
       */
      realpath: string;

      dirname?: string;
      /**
       * 图片在数组中index
       */
      index?: number;
    },
    setSize?: boolean,
    toCenter?: boolean
  ) {
    const layer = this.layer;
    if (!layer) return;

    const { resourceName, colorGroup, realpath, index, dirname } = data;

    // 添加图片到store
    const imageStore = useImagesStore();
    const _realpaths = [
      ...this.getResources({
        resourceName,
        colorGroup
      })
    ];
    const { basename, value } =
      (await imageStore.uploadImage(realpath, {
        dirname,
        md5: !!GlobalConfig.imageMd5
      })) ?? {};
    if (!basename || !value) return;

    if (index !== undefined) {
      // 资源为数组格式
      for (let i = 0; i <= index; i++) {
        if (_realpaths[i] == undefined) _realpaths[i] = '';
      }
      _realpaths[index] = basename;
    } else {
      // 非数组，确保只有一个元素即可
      _realpaths.length = 0;
      _realpaths.push(basename);
    }

    // 根据图片大小，修改shape的大小
    if (setSize) {
      const { width, height } = (await getImageSize(realpath, value)) ?? {};
      this._setAttrs({ w: width ?? 0, h: height ?? 0 });
      if (toCenter) {
        const watchinfo = useWatchfaceStore();
        this._setAttrs({
          x: round(watchinfo.size.width / 2 - (width ?? 100) / 2),
          y: round(watchinfo.size.height / 2 - (height ?? 100) / 2)
        });
      }
    }

    if (!layer.attrs.resources) layer.attrs.resources = {};
    if (!layer.attrs.resources?.[resourceName])
      layer.attrs.resources[resourceName] = {};

    layer.attrs.resources[resourceName][colorGroup] = _realpaths;

    this.initAttrs({
      attrs: ['resources']
    });
    emitter.emit(EventType['shape:upload-resoueces'], { shapeId: this.id });
  }
  /**
   * 批量上传图组或序列帧图片
   */
  async uploadResources(data: {
    resourceName: ResourceKey;
    /**
     * 配色id
     */
    colorGroup: string;

    /**
     * 图片真实目录名
     */
    realpaths: string[];

    dirname?: string;
    resize?: boolean;
  }) {
    const layer = this.layer;
    if (!layer) return;

    const {
      resourceName,
      colorGroup,
      realpaths,
      dirname,
      resize = true
    } = data;

    // 添加图片到store
    const imageStore = useImagesStore();
    const _basenames = [
      ...this.getResources({
        resourceName,
        colorGroup
      })
    ];

    // 批量上传
    const _basenamesNew =
      (await imageStore.uploadImages(realpaths, {
        dirname,
        md5: !!GlobalConfig.imageMd5
      })) ?? [];
    _basenamesNew.forEach((_basename, i) => {
      if (_basename) _basenames[i] = _basename;
    });

    // 判断是否重置尺寸
    const { width, height } = imageStore.imagesMap[_basenames?.[0] ?? ''] ?? {};
    if (resize) {
      if (layer.attrs.w != width) layer.attrs.w = width;
      if (layer.attrs.h != height) layer.attrs.h = height;
    }

    if (!layer.attrs.resources) layer.attrs.resources = {};
    if (!layer.attrs.resources?.[resourceName])
      layer.attrs.resources[resourceName] = {};

    layer.attrs.resources[resourceName][colorGroup] = _basenames;

    this.initAttrs({
      attrs: ['resources']
    });
    emitter.emit(EventType['shape:upload-resoueces'], { shapeId: this.id });
  }

  /**
   * 删除资源，如未提供resourceName，则删除全部；如未提供colorGroup，则删除resourceName所有资源
   * @param resourceName 【可选】
   * @param colorGroup 【可选】
   */
  deleteResource(data: {
    resourceName?: ResourceKey;
    colorGroup?: string;
    index?: number;
  }) {
    const layer = this.layer;
    if (!layer) return;

    const { resourceName, colorGroup, index } = data;
    // 删除所有资源
    if (!resourceName) {
      layer.attrs.resources = {};
    } else {
      // 删除具体资源下所有配色
      if (!colorGroup) {
        const resources = layer.attrs?.resources ?? {};
        delete resources[resourceName];
        layer.attrs.resources = resources;
      } else {
        // 删除具体资源下-具体配色的资源
        const colorGroupObj = layer.attrs?.resources?.[resourceName] ?? {};
        if (index == undefined) {
          // 删除具体资源下-具体配色的全部资源
          delete colorGroupObj[colorGroup];
        } else {
          colorGroupObj[colorGroup]?.splice(index, 1, '');
        }
        if (layer.attrs?.resources?.[resourceName]) {
          layer.attrs.resources[resourceName] = colorGroupObj;
        }
      }
    }

    this.initAttrs({ attrs: ['resources'] });
  }

  /**
   * 匹配资源长度
   * @param resourceName 【可选】
   * @param colorGroup 【可选】
   */
  matchResourceLength(data: { resourceName: ResourceKey; length: number }) {
    const layer = this.layer;
    if (!layer) return;

    const { resourceName, length } = data;
    const colorIds = Object.keys(layer.attrs.resources?.[resourceName] ?? {});
    colorIds.forEach((colorId) => {
      const srcs = layer.attrs.resources?.[resourceName]?.[colorId];
      if (!srcs?.length || !layer.attrs.resources?.[resourceName]?.[colorId])
        return;
      const len1 = srcs.length;
      if (len1 > length) {
        srcs.splice(length, len1 - length);
      } else if (len1 < length) {
        for (let i = 1; i <= length - len1; i++) {
          srcs.push('');
        }
      }
    });
    this.initAttrs({ attrs: ['resources'] });
  }

  /**
   *当前元素绑定的数据源，有可能是假数据源
   *
   * @readonly
   * @memberof ShapeAttribute
   */
  get sourceItem() {
    const item = dataSourceWholeItems.find(
      (item) => item.key == this.attrs.source
    );
    return item;
  }

  /**
   * 数据源集合，如果为多单位则元素为子单位数据源（2个），否则为主数据源（1个）
   *
   * @readonly
   * @memberof ShapeAttribute
   */
  get sourceItems() {
    const item0 = this.sourceItem;
    if (this.isMultiUnitSource) {
      const items =
        item0?.children?.map((key) =>
          dataSourceWholeItems.find((item) => item.key == key)
        ) ?? [];

      // 如果是温度类，需要判断是否跟随系统，如果不是需要返回特定的数据源
      const _supportTemperatureType = supportTemperatureType(
        this.type,
        item0?.key ?? ''
      );
      const temperatureUnitType = this.attrs.temperatureUnitType ?? 'celsius';
      return items.filter((item) => {
        // 无需判断（非温度类或选择了跟随系统）
        if (!_supportTemperatureType) return true;
        if (temperatureUnitType == 'system') {
          return true;
        } else if (temperatureUnitType == 'celsius') {
          // 摄氏度，不能含有华氏度
          return !item?.key?.includes('Fahrenheit');
        } else {
          // 华氏度，必须有华氏度
          return item?.key?.includes('Fahrenheit');
        }
      });
    } else {
      return [item0];
    }
  }

  /**
   * 是否支持多单位数据源
   *
   * @readonly
   * @memberof ShapeAttribute
   */
  get supportMultiSource() {
    const type = this.type;
    return (
      type == ShapeType.ProgressBar ||
      (type == ShapeType.ImageArray && this.attrs.fillType == '数字填充')
    );
  }

  get isMultiUnitSource() {
    return this.supportMultiSource && this.sourceItem?.tag == 'compound';
  }

  /**
   * 获取使用的所有数据源，如果是文本则包括文本所有数据源，如果不是文本则表示多单位数据源
   *
   * @readonly
   * @memberof ShapeAttribute
   */
  get soueceKeys() {
    const keys: string[] = [];
    if (this.type == ShapeType.Text) {
      this.attrs.strings?.forEach((item) => {
        if (item.isSource) keys.push(item.value);
      });
    } else {
      keys.push(...this.sourceItems.map((item) => item?.key ?? ''));
    }

    return keys.filter((key) => !!key);
  }

  /**
   * 是否是自动布局
   *
   * @readonly
   * @memberof ShapeAttribute
   */
  get isFlexLayout() {
    const {
      flexDirection = 'disabled',
      justifyContent,
      alignItems
    } = this.attrs;
    return (
      this.type == ShapeType.Widget &&
      ['column', 'row'].includes(flexDirection) &&
      !!justifyContent &&
      !!alignItems
    );
  }

  /**
   * 是否使用自定义字体
   *
   * @readonly
   * @memberof ShapeAttribute
   */
  get usingCustomFont() {
    return this.attrs.fontId == 'custom';
  }

  /**
   * 获取图片大小,（多图时返回第一个有值的图片大小）
   * @param resourceName 资源名
   * @param colorId 配色id，默认为第一个配色
   * @returns
   */
  getImageSize = (resourceName: ResourceKey, colorId: string) => {
    const images = this.attrs.resources?.[resourceName] ?? {};

    const imageSrcs = images[colorId] ?? [];
    const _index = imageSrcs.findIndex((item) => !!item);
    const imageSrc = imageSrcs[_index];
    if (!imageSrc) return;

    const res = { width: 0, height: 0 };
    const { width = 0, height = 0 } =
      useImagesStore().imagesMap[imageSrc] ?? {};
    res.width = width;
    res.height = height;
    return res;
  };

  initAttrs(emit?: { attrs: (keyof ShapeAttribute['attrs'])[] }) {
    const layer = this.layer;
    this.attrs = {
      ...this.attrs,
      ...layer?.attrs
    };

    if (emit)
      emitter.emit(EventType['shape:set-attr'], {
        shapeId: this.id,
        attrs: emit.attrs
      });
  }

  reverseChildren() {
    const layer = this.layer;
    layer?.children?.reverse();
    this.initAttrs({ attrs: ['pivotY'] });
  }

  /**
   * 获取sprite图片或视频资源
   * @param sort 是否按 extraIdToPlay 排序
   * @param colorGroup 可选，指定配色/样式 id，用于获取该样式下的视频数据
   */
  getSpriteMulitItems(sort?: boolean, colorGroup?: string) {
    const attrs = this.attrs;
    const {
      extraIdToPlay = '',
      spriteExtra = [],
      videoSrc,
      videoName,
      videoFps,
      videoColorData,
      spriteDefaultIndex,
      w = 0,
      h = 0
    } = attrs;

    // 解析主视频的 colorGroup 数据
    const mainColorVideo =
      colorGroup && videoColorData?.[colorGroup]?.videoSrc
        ? videoColorData[colorGroup]
        : colorGroup && videoColorData
        ? Object.values(videoColorData).find((v) => !!v?.videoSrc)
        : undefined;

    const items: SpriteAttribute['spriteExtra'] = [
      {
        id: '',
        w,
        h,
        videoSrc: mainColorVideo?.videoSrc ?? videoSrc,
        videoName: mainColorVideo?.videoName ?? videoName,
        videoFps: mainColorVideo?.videoFps ?? videoFps,
        spriteDefaultIndex,
        spriteImagesName: 'images'
      },
      ...spriteExtra.map((item) => {
        // 解析 spriteExtra 中每项的 colorGroup 数据
        const extraColorVideo =
          colorGroup && item.videoColorData?.[colorGroup]?.videoSrc
            ? item.videoColorData[colorGroup]
            : colorGroup && item.videoColorData
            ? Object.values(item.videoColorData).find((v) => !!v?.videoSrc)
            : undefined;
        return {
          ...item,
          videoSrc: extraColorVideo?.videoSrc ?? item.videoSrc,
          videoName: extraColorVideo?.videoName ?? item.videoName,
          videoFps: extraColorVideo?.videoFps ?? item.videoFps
        };
      })
    ];

    // 进行排序，extraIdToPlay在最前面
    if (sort) {
      return [
        ...items.filter((item) => item.id == extraIdToPlay),
        ...items.filter((item) => item.id != extraIdToPlay)
      ];
    } else {
      return items;
    }
  }
}
